/**
 * The application shell: sidebar, header, outlet.
 *
 * The operator drawer is gone — the operator is a primary screen now
 * (`#/operator`), because a conversation with the agent that runs the team
 * deserves a history, files and a real composer, none of which fit a drawer.
 *
 * The §16.5 obligations that ADR-103 was written to protect are all still here,
 * and they cost the same in a component as they did by hand:
 *
 *  - Landmarks are explicit (`banner`, `navigation`, `main`, `complementary`),
 *    so a screen-reader user moves between regions instead of tabbing through
 *    everything between them.
 *  - One polite live region for the whole application. More than one competing
 *    region is a reliable way to have none of them heard.
 *  - `<main>` takes focus on every route change and the live region is given the
 *    screen's name, which is what a real page navigation would have done for
 *    free.
 *
 * What React changed is that these are now three lines in one place rather than
 * a manual `render()` per region.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { Link, Outlet, useLocation } from 'react-router';
import { ActionMenu, ActionList, NavList, Button } from '@primer/react';
import {
  ProjectIcon,
  ProjectRoadmapIcon,
  CommentDiscussionIcon,
  PeopleIcon,
  GearIcon,
  RocketIcon,
  LightBulbIcon,
} from '@primer/octicons-react';

import { PRIMARY_SCREENS, SCREENS, SETTINGS_SCREENS, screenMeta, type ScreenId } from '../logic/route.js';

/* Octicons only — the rule of this branch: nothing hand-drawn where Primer
   already ships the glyph. */
const NAV_ICONS: Readonly<Partial<Record<ScreenId, typeof ProjectIcon>>> = {
  board: ProjectIcon,
  epics: ProjectRoadmapIcon,
  operator: CommentDiscussionIcon,
  overview: PeopleIcon,
  proposals: LightBulbIcon,
  releases: RocketIcon,
};
import { useStore, useStoreState } from './store-context.js';
import { HealthLine } from './components/health-line.js';
import { ViewOptions } from './components/view-options.js';
import { useReleaseEventInvalidation, useTeams } from './queries.js';
import { useQueryClient } from '@tanstack/react-query';
import { useConsoleMutation } from './queries.js';
import { signOutConsole } from '../firebase-auth.js';

/**
 * Maps a path back to the screen it belongs to, for the header.
 *
 * Over the whole table rather than the menus: detail screens are reached by
 * clicking a thing and appear in neither, but they still have a name.
 */
function screenForPath(pathname: string): ScreenId {
  const first = pathname.split('/').filter(Boolean)[0] ?? '';
  return SCREENS.find((screen) => screen.path === `/${first}`)?.id ?? 'board';
}

export function Shell(): ReactNode {
  const location = useLocation();
  const store = useStore();
  const teamName = store.config.teamName;
  const announcement = useStoreState((state) => state.announcement);
  useReleaseEventInvalidation();

  const mainRef = useRef<HTMLElement>(null);

  const screen = screenForPath(location.pathname);
  const meta = screenMeta(screen);

  /*
   * Keyed on the screen, not the path.
   *
   * `#/board/{card}` is the same screen with something open on it (ADR-118).
   * Keying this on the path would pull focus back to the main landmark every
   * time a card opened and re-announce "Board" over it; what opened announces
   * and focuses itself.
   */
  useEffect(() => {
    // Deployment detail owns a more specific heading focus once its query resolves.
    if (screen === 'deployment') return;
    // A single-page navigation leaves focus on a link that no longer exists.
    mainRef.current?.focus({ preventScroll: false });
    store.announce(`${meta.title}. ${meta.purpose}`);
  }, [screen, meta.title, meta.purpose, store]);

  useEffect(() => {
    store.startStream();
    return () => store.stopStream();
  }, [store]);

  return (
    <div className="shell">
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>

      <div className="shell-columns">
        <div className="shell-sidebar">
          <div className="shell-brand">
            <p className="product-name">
              <svg className="brand-glyph" viewBox="0 0 16 16" width="18" height="18" aria-hidden>
                <rect x="1.5" y="1.5" width="13" height="13" rx="3.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
                <circle cx="5.6" cy="6.4" r="1.15" fill="currentColor" />
                <circle cx="10.4" cy="6.4" r="1.15" fill="currentColor" />
                <path d="M5.2 10.2c.8.9 1.7 1.35 2.8 1.35s2-.45 2.8-1.35" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
              Autonomous Agents
            </p>
            <TeamSwitcher fallbackName={teamName} />
          </div>
          <Navigation />
        </div>

        <div className="shell-body">
          <header className="shell-header">
            <div className="shell-title">
              <h1 className="screen-heading">{meta.title}</h1>
              <p className="screen-purpose">{meta.purpose}</p>
            </div>
            <div className="shell-controls">
              <HealthLine />
              <ViewOptions />
              {store.config.firebase === null ? null : <Button onClick={() => void signOutConsole()}>Выйти</Button>}
            </div>
          </header>

          <main className="shell-main" id="main-content" tabIndex={-1} ref={mainRef}>
            <div className="screen-outlet">
              <Outlet />
            </div>
          </main>
        </div>
      </div>

      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {announcement}
      </div>
    </div>
  );
}

/**
 * The team this Console is looking at (§multi-team): the current team's name
 * opens a menu of every team on this control plane, plus «New team…», which
 * bootstraps one from the delivery template and switches to it.
 */
function TeamSwitcher({ fallbackName }: { fallbackName: string | null }): ReactNode {
  const store = useStore();
  const queryClient = useQueryClient();
  const currentTeamId = useStoreState((state) => state.currentTeamId);
  const teams = useTeams();
  const bootstrap = useConsoleMutation<{ slug: string; displayName: string }, { teamId: string }>(
    (data, context, input) => data.bootstrapTeam(input.slug, input.displayName, context),
    [['teams']],
  );

  const current = (teams.data ?? []).find((team) => team.teamId === currentTeamId) ?? null;
  const label = current?.displayName ?? fallbackName ?? 'No team active';

  const switchTo = (teamId: string): void => {
    store.setTeam(teamId);
    // Every screen belongs to the newly chosen team: drop all cached answers.
    void queryClient.invalidateQueries();
  };

  return (
    <ActionMenu>
      <ActionMenu.Button variant="invisible" className="team-switcher" size="small">
        {label}
      </ActionMenu.Button>
      <ActionMenu.Overlay width="medium">
        <ActionList selectionVariant="single">
          {(teams.data ?? []).map((team) => (
            <ActionList.Item key={team.teamId} selected={team.teamId === currentTeamId} onSelect={() => switchTo(team.teamId)}>
              {team.displayName}
              <ActionList.Description>{team.slug}</ActionList.Description>
            </ActionList.Item>
          ))}
          <ActionList.Divider />
          <ActionList.Item
            onSelect={() => {
              const slug = globalThis.prompt?.('New team slug (lowercase, dashes):');
              if (slug === null || slug === undefined || slug.trim().length === 0) return;
              bootstrap.mutate(
                { slug: slug.trim(), displayName: slug.trim() },
                {
                  onSuccess: (team) => switchTo(team.teamId),
                  // Failure was silent — «nothing happened». Say why, so a taken
                  // slug or a server refusal reads as a reason, not a dead button.
                  onError: (error) => globalThis.alert?.(`Could not create the team: ${String((error as Error)?.message ?? error)}`),
                },
              );
            }}
          >
            New team…
          </ActionList.Item>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  );
}

function Navigation(): ReactNode {
  const location = useLocation();
  const inSettings = SETTINGS_SCREENS.some((screen) => location.pathname.startsWith(screen.path));

  /*
   * Primer's NavList, whole: rows, spacing, hover, the aria-current styling
   * and the settings sub-list are all its own. The router's Link rides
   * underneath via `as`, which is exactly the pairing Primer documents.
   */
  return (
    <NavList aria-label="Sections">
      {PRIMARY_SCREENS.map((screen) => {
        const Icon = NAV_ICONS[screen.id];
        /* NavList styles the selected row from its own aria-current prop, so
           the router's answer is passed explicitly rather than trusted to
           reach the anchor attribute underneath. */
        const active =
          location.pathname.startsWith(screen.path) ||
          (screen.id === 'releases' && location.pathname.startsWith('/deployments/'));
        return (
          <NavList.Item key={screen.id} as={Link} to={screen.path} {...(active ? { 'aria-current': 'page' as const } : {})}>
            {Icon !== undefined ? (
              <NavList.LeadingVisual>
                <Icon />
              </NavList.LeadingVisual>
            ) : null}
            {screen.title}
            <span className="sr-only">{screen.purpose}</span>
          </NavList.Item>
        );
      })}

      <NavList.Group>
        <NavList.Item defaultOpen={inSettings}>
          <NavList.LeadingVisual>
            <GearIcon />
          </NavList.LeadingVisual>
          Settings
          <NavList.SubNav>
            {SETTINGS_SCREENS.map((screen) => (
              <NavList.Item
                key={screen.id}
                as={Link}
                to={screen.path}
                {...(location.pathname.startsWith(screen.path) ? { 'aria-current': 'page' as const } : {})}
              >
                {screen.title}
              </NavList.Item>
            ))}
          </NavList.SubNav>
        </NavList.Item>
      </NavList.Group>
    </NavList>
  );
}
